import React, { useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Checkbox,
  ClickAwayListener,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { DateTime } from 'luxon';
import {
  AccessTime,
  Close,
  Close as CloseIcon,
  Edit,
  ExitToApp,
  ExpandMore,
  Group,
  HourglassEmpty,
  SyncRounded,
} from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { unpackToDate, unpackToDateTime, unpackToTime } from '@campfire/hot-date';
import { TabletButton } from '@campfire/tablet-button';
import { convertFromRaw, EditorState } from 'draft-js';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { useCampfireTheme } from '../../../../../../../theme/useCampfireTheme';
import { useApiUrl } from '../../../../../../../global/config/useApiUrl';
import { useOrgInfo } from '../../../../../../../global/auth/use-org-info/use-org-info';
import { useCampfireFetch } from '../../../../../../../global/network/useCampfireFetch';
import { useSnackbar } from '../../../../../../../global/config/useSnackbar';
import { SnackbarContextProps } from '../../../../../../../global/config/SnackbarContext';
import { GetIncomingVolunteerProfile_vm_profile_application_applicationActivities as ApplicationActivities } from '../../../__generated__/GetIncomingVolunteerProfile';
import { getDisplayTimeSchedule } from '../../../../../../../common/functions/activity-display-helpers';

interface IncomingVolunteerTaskViewProps {
  onClose: () => void;
  applicationActivityId?: string;
  reload?: () => void;
  setSnackbar?: (props: SnackbarContextProps | undefined) => void;
  activityApplication?: ApplicationActivities;
}

const useTaskListItemClasses = makeStyles((theme: Theme) =>
  createStyles({
    listItem: {
      border: '.6px solid #00000010',
      padding: '15px 30px',
      backgroundColor: '#fff',
    },
    checkIcon: {
      fontSize: 29,
      color: '#00ed7e',
    },
    circleIcon: {
      fontSize: 29,
      color: theme.palette.grey[200],
    },
  })
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    menuItem: {
      display: 'flex',
      datesContainer: {
        background: theme.color.grey.neutralBrand100,
        borderRadius: 4,
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
      },
      dateGridItem: {
        background: theme.color.white.neutral50,
        padding: '6px',
        boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
        borderRadius: '8px',
        '&:hover': {
          background: theme.palette.grey[200],
        },
      },
      padding: '0.3rem',
      borderRadius: '4px',
      cursor: 'pointer',
      alignItems: 'center',
      '&:hover': {
        opacity: 0.75,
      },
    },
    menuItemText: {
      marginLeft: '0.3rem',
      fontSize: '14px',
    },
    menuItemTextInstructions: {
      marginLeft: '0.3rem',
      fontSize: '12px',
    },
    tooltip: {
      borderRadius: '12px',
      paddingTop: '0.5rem',
      paddingLeft: '.7rem',
      paddingRight: '.7rem',
      paddingBottom: '0.5rem',
      backgroundColor: theme.color.tooltip.background,
    },
    tooltipArrow: {
      '&::before': {
        backgroundColor: theme.color.tooltip.background,
      },
    },
    datesContainer: {
      background: theme.color.grey.neutralBrand100,
      borderRadius: 4,
      display: 'flex',
      flexWrap: 'wrap',
      flex: 1,
    },
    dateGridItem: {
      background: theme.color.white.neutral50,
      padding: '6px',
      boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
      borderRadius: '8px',
      '&:hover': {
        background: theme.palette.grey[200],
      },
    },
    title: {
      color: theme.color.grey.neutral400,
      fontWeight: 600,
      width: '100%',
      letterSpacing: '-0.02em',
      fontSize: '13px',
    },
    subTitle: {
      fontSize: 11,
      color: theme.color.grey.neutral200,
    },
    subTitlesContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    subTitleContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    icon: {
      marginRight: '7px',
      color: theme.color.grey.neutral200,
    },
  })
);

export const IncomingVolunteerActivityView = ({
  onClose,
  reload,
  activityApplication,
}: IncomingVolunteerTaskViewProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saveHandler, setSaveHandler] = useState<(() => void) | null>(null);
  const isEvent = activityApplication?.activity.isEvent && (activityApplication as any)?.eventSelections;

  return activityApplication ? (
    <>
      <Box width={1}>
        <Box
          borderBottom='1px solid #e0e0e0'
          display='flex'
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          px={3}
          py={1}
        >
          <Typography>Activity Application</Typography>
          <Box display='flex' alignItems='center' style={{ gap: '8px' }}>
            {isEvent && (
              <>
                {isEditing && saveHandler ? (
                  <TabletButton
                    variant='contained'
                    color='primary'
                    onClick={() => {
                      saveHandler();
                      setIsEditing(false);
                    }}
                  >
                    Save
                  </TabletButton>
                ) : (
                  <TabletButton
                    variant='outlined'
                    onClick={() => setIsEditing(true)}
                    startIcon={<Edit />}
                    style={{ borderRadius: '4px', paddingLeft: '4px', paddingRight: '4px' }}
                  >
                    Edit
                  </TabletButton>
                )}
              </>
            )}
            <Box marginLeft={1}>
              <IconButton onClick={() => onClose()}>
                <CloseIcon color='action' />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box flex={1} style={{ overflowY: 'scroll', backgroundColor: '#f6f8f9', width: '100%' }} display='flex'>
        <Box position='relative' display='flex' flex='1 1 auto' height={'auto'} minHeight={1} justifyContent={'center'}>
          <ActivityListItem
            appliedActivity={activityApplication}
            reload={reload}
            isEditing={isEditing}
            onRegisterSave={(handler: () => void) => setSaveHandler(() => handler)}
          />
        </Box>
      </Box>
    </>
  ) : (
    <></>
  );
};

interface ActivityProps {
  appliedActivity?: ApplicationActivities;
  reload?: () => void;
  isEditing?: boolean;
  onRegisterSave?: (handler: () => void) => void;
}

const ActivityListItem = (props: ActivityProps) => {
  const taskListItemClasses = useTaskListItemClasses();
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const { appliedActivity, reload, isEditing, onRegisterSave } = props;
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });
  const { setSnackbar } = useSnackbar();
  const [withdrawOpen, setWithdrawOpen] = useState<boolean>(false);

  const applicationStatus = () => {
    if (appliedActivity?.status === 'pending') {
      return 'unsure';
    }
    if (appliedActivity?.status === 'to-team') {
      return 'rostered';
    }
    if (appliedActivity?.status === 'to-application') {
      return 'applied';
    }
    if (appliedActivity?.status === 'to-waitlist') {
      return 'waitlisted';
    }
    if (appliedActivity?.status === 'decline') {
      return 'rejected';
    }
    return 'available';
  };

  const applicationStatusDesc = () => {
    if (appliedActivity?.status === 'pending') {
      return 'Pending';
    }
    if (appliedActivity?.status === 'to-team') {
      return 'To Team';
    }
    if (appliedActivity?.status === 'to-application') {
      return 'To Application';
    }
    if (appliedActivity?.status === 'to-waitlist') {
      return 'To Waitlist';
    }
    if (appliedActivity?.status === 'decline') {
      return 'Rejected';
    }
    return 'Apply';
  };

  const applicationStatusIcon = () => {
    if (appliedActivity?.status === 'pending') {
      return <ExpandMore style={{ color: theme.color.grey.neutral300, marginLeft: '-2px' }} />;
    }
    if (appliedActivity?.status === 'approved') {
      return <ExpandMore style={{ color: theme.color.grey.neutral300, marginLeft: '-2px' }} />;
    }
    if (appliedActivity?.status === 'rejected') {
      return <ExpandMore style={{ color: theme.color.grey.neutral300, marginLeft: '-2px' }} />;
    }
    return <ExpandMore style={{ color: theme.color.grey.neutral300, marginLeft: '-2px' }} />;
  };

  const onActivityUpdate = (activityId: string, status: string) => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/application/activity-application/update-status`,
        method: 'post',
        data: {
          applicationActivityId: activityId,
          status: status,
        },
      })
      .then((response) => {
        if (reload) reload();
        if (response.ok) {
          setSnackbar({
            variant: 'success',
            open: true,
            message: 'Status Updated',
          });
        } else {
          setSnackbar({
            variant: 'error',
            open: true,
            message: 'Error updating status',
          });
        }
      });
  };
  const managerName = appliedActivity?.actionedBy?.profile.preferredName
    ? `${appliedActivity?.actionedBy?.profile.preferredName} ${appliedActivity?.actionedBy.profile.lastName}`
    : `${appliedActivity?.actionedBy?.profile.firstName} ${appliedActivity?.actionedBy?.profile.lastName}`;

  const statusColor = applicationStatus();
  const recurrence =
    appliedActivity?.activity.__typename === 'VOLUNTEER_RecurringActivityType'
      ? appliedActivity?.activity.schedule.recurrences[0].humanReadable
      : 'Flexible';

  const formatTime = (time: string) => unpackToTime(time).toFormat('h:mm a');
  const editorState = React.useMemo(
    () =>
      appliedActivity?.activity.description
        ? EditorState.createWithContent(convertFromRaw(JSON.parse(appliedActivity?.activity.description)))
        : null,
    [appliedActivity?.activity.description]
  );

  const availabilitySessions = (appliedActivity?.availabilitySessions as unknown) as { sessionIds: string };
  const availabilityDates = (appliedActivity?.availabilityDates as unknown) as string[];

  return (
    <List style={{ paddingBottom: '0', paddingTop: '10px', alignItems: 'center', flexGrow: 1, margin: '1em 2em' }}>
      <ListItem className={taskListItemClasses.listItem} style={{ alignItems: 'flex-start' }}>
        <ListItemText>
          <Typography
            style={{ fontWeight: 600, fontSize: '15px', display: 'flex', alignItems: 'center', color: '#444' }}
          >
            {appliedActivity?.activity.name}
          </Typography>
          <Box display='flex' alignItems='center' paddingTop={'2px'}>
            <SyncRounded style={{ color: theme.color.grey.neutral200, fontSize: 16, marginRight: '6px' }} />
            <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200 }}>
              {recurrence}
              {appliedActivity?.activity.startTime && appliedActivity?.activity.endTime
                ? `, ${formatTime(appliedActivity?.activity.startTime)} - ${formatTime(
                    appliedActivity?.activity.endTime
                  )}`
                : ''}
            </Typography>
          </Box>
        </ListItemText>
        <ListItemIcon style={{ paddingLeft: '15px', paddingTop: '7px' }}>
          <ClickAwayListener onClickAway={() => setWithdrawOpen(false)}>
            <Tooltip
              arrow
              interactive
              title={
                <Box>
                  {appliedActivity?.status !== 'to-team' && (
                    <Box
                      className={classes.menuItem}
                      onClick={() =>
                        appliedActivity ? onActivityUpdate(appliedActivity.applicationActivityId, 'to-team') : undefined
                      }
                      style={{ color: theme.alert.green.light }}
                    >
                      <Group style={{ fontSize: '17px', marginRight: '5px' }} />
                      <Typography className={classes.menuItemText}>To Team</Typography>
                    </Box>
                  )}
                  {appliedActivity?.status !== 'to-application' && (
                    <Box
                      className={classes.menuItem}
                      onClick={() =>
                        appliedActivity
                          ? onActivityUpdate(appliedActivity.applicationActivityId, 'to-application')
                          : undefined
                      }
                      style={{ color: theme.color.enrolmenStatuses.applied.primary }}
                    >
                      <ExitToApp style={{ fontSize: '17px', marginRight: '5px' }} />
                      <Typography className={classes.menuItemText}>To Application</Typography>
                    </Box>
                  )}
                  {appliedActivity?.status !== 'to-waitlist' && (
                    <Box
                      className={classes.menuItem}
                      onClick={() =>
                        appliedActivity
                          ? onActivityUpdate(appliedActivity.applicationActivityId, 'to-waitlist')
                          : undefined
                      }
                      style={{ color: theme.color.enrolmenStatuses.waitlist.primary }}
                    >
                      <HourglassEmpty style={{ fontSize: '17px', marginRight: '5px' }} />
                      <Typography className={classes.menuItemText}>To Waitlist</Typography>
                    </Box>
                  )}
                  {appliedActivity?.status !== 'decline' && (
                    <Box
                      className={classes.menuItem}
                      onClick={() =>
                        appliedActivity ? onActivityUpdate(appliedActivity.applicationActivityId, 'decline') : undefined
                      }
                      style={{ color: theme.alert.red.light }}
                    >
                      <Close style={{ fontSize: '17px', marginRight: '5px' }} />
                      <Typography className={classes.menuItemText}>Reject</Typography>
                    </Box>
                  )}
                </Box>
              }
              disableFocusListener
              disableHoverListener
              disableTouchListener
              onClick={() => setWithdrawOpen(true)}
              placement='bottom-end'
              open={withdrawOpen}
              onClose={() => setWithdrawOpen(false)}
              onOpen={() => setWithdrawOpen(true)}
              classes={{
                tooltip: classes.tooltip,
                arrow: classes.tooltipArrow,
              }}
            >
              <TabletButton
                data-track='resources-add-new-menu-button'
                variant='outlined'
                color='primary'
                aria-controls='add-new'
                aria-haspopup='true'
                endIcon={applicationStatusIcon()}
                style={{ padding: '8px 12px', border: `1px solid #DCDCDC`, whiteSpace: 'nowrap' }}
              >
                <Box display='flex' alignItems='center'>
                  <Box
                    height='10px'
                    width='10px'
                    borderRadius='50%'
                    marginRight='8px'
                    bgcolor={theme.color.rosters.status[statusColor].primary}
                  />
                  <Typography
                    variant='caption'
                    style={{
                      fontSize: '12px',
                      fontWeight: 700,
                      color: theme.color.grey.neutral400,
                      textTransform: 'uppercase',
                      lineHeight: '14px',
                      marginTop: '1px',
                    }}
                  >
                    {applicationStatusDesc()}
                  </Typography>
                </Box>
              </TabletButton>
            </Tooltip>
          </ClickAwayListener>
        </ListItemIcon>
      </ListItem>
      <ListItem className={taskListItemClasses.listItem} style={{ alignItems: 'flex-start' }}>
        <ListItemText>
          <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080', paddingTop: '5px' }}>
            {editorState && <CampfireRichEditor readOnly editorState={editorState} />}
          </Typography>
          {appliedActivity?.activity.isEvent && (appliedActivity as any)?.eventSelections ? (
            <EventSelectionsEditor
              appliedActivity={appliedActivity}
              reload={reload}
              isEditing={isEditing}
              onRegisterSave={onRegisterSave}
            />
          ) : availabilityDates && availabilitySessions ? (
            <>
              <Box>
                <Typography
                  style={{
                    fontWeight: 600,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#444',
                    paddingTop: '10px',
                    paddingBottom: '5px',
                  }}
                >
                  Availability - Sessions
                </Typography>
              </Box>
              <Box className={classes.datesContainer} padding={'12px 8px 8px'} justifyContent={'unset'}>
                {appliedActivity?.activity.sessions.map((session) => {
                  return (
                    <Box style={{ backgroundColor: '#fff', padding: '.5em 1em', borderRadius: '10px' }}>
                      <Typography className={classes.title}>{session.name}</Typography>
                      <Box>
                        <Box className={classes.subTitlesContainer}>
                          <Box className={classes.subTitleContainer}>
                            <AccessTime style={{ fontSize: 17 }} className={classes.icon} />
                            <Typography className={classes.subTitle}>
                              {getDisplayTimeSchedule(session.startTime, session.endTime)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Box>
                <Typography
                  style={{
                    fontWeight: 600,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#444',
                    paddingTop: '10px',
                    paddingBottom: '5px',
                  }}
                >
                  Availability - Dates
                </Typography>
              </Box>
              <Box className={classes.datesContainer} padding={'12px 8px 8px'} justifyContent={'unset'}>
                {appliedActivity?.activity.occurrencesBetween.map((date: any) => {
                  return (
                    <Box key={date} margin='4px'>
                      <Box display='flex' justifyContent='center' alignItems='center'>
                        <ButtonBase
                          disableRipple
                          disableTouchRipple
                          disabled
                          className={classes.dateGridItem}
                          data-track='fs-volPrf-enrolment-roster-date-item'
                          style={{
                            backgroundColor: undefined,
                            borderBottom: availabilityDates.find((a) => a === date)
                              ? `8px solid ${theme.color.rosters.status.available.primary}`
                              : `8px solid ${theme.color.rosters.status.unavailable.primary}`,
                            minWidth: '87px',
                          }}
                        >
                          <Typography variant='body2' style={{ fontSize: '11px' }}>
                            {unpackToDate(date).toLocaleString(DateTime.DATE_MED)}
                          </Typography>
                        </ButtonBase>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          ) : null}
        </ListItemText>
      </ListItem>

      {appliedActivity?.dateActioned && appliedActivity.actionedBy ? (
        <ListItem className={taskListItemClasses.listItem} style={{ marginTop: '15px' }}>
          <ListItemText>
            <Typography
              style={{ fontWeight: 600, fontSize: '13px', display: 'flex', alignItems: 'center', color: '#444' }}
            >
              Status
              <br />
            </Typography>
            <Typography variant={'body2'} style={{ fontSize: '12px', color: '#808080' }}>
              <span style={{ textTransform: 'capitalize' }}>{applicationStatusDesc()}</span> by {managerName},{' '}
              {unpackToDateTime(appliedActivity.dateActioned).toFormat('DDDD')}
            </Typography>
          </ListItemText>
        </ListItem>
      ) : (
        undefined
      )}

      <ListItem
        className={taskListItemClasses.listItem}
        style={{ marginTop: '2em', backgroundColor: 'transparent', border: 'none', padding: '0 1.5em' }}
      >
        <ListItemText>
          <Typography
            style={{ fontWeight: 600, fontSize: '13px', display: 'flex', alignItems: 'center', color: '#666' }}
          >
            Application Actions
          </Typography>
          <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080' }}>
            <Box style={{ display: 'flex', alignItems: 'center', paddingBottom: '5px' }}>
              <Group style={{ fontSize: '17px' }} />
              <Typography className={classes.menuItemTextInstructions}>
                <strong style={{ paddingRight: '5px' }}>
                  {appliedActivity?.activity.isEvent
                    ? appliedActivity?.activity.hasOpenRoster
                      ? 'To Rostered Team'
                      : 'To Available Team'
                    : 'To Team'}
                </strong>
                {appliedActivity?.activity.isEvent
                  ? appliedActivity?.activity.hasOpenRoster
                    ? 'User will be rostered to their selected sessions when their profile is approved'
                    : 'User will be marked as available for their selected sessions when their profile is approved'
                  : 'Users will be assigned into the activity team when their application is approved'}
              </Typography>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', paddingBottom: '5px' }}>
              <ExitToApp style={{ fontSize: '17px' }} />
              <Typography className={classes.menuItemTextInstructions}>
                <strong style={{ paddingRight: '5px' }}>To Application</strong>
                Users will be assigned to the activity application list when their application is approved
              </Typography>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', paddingBottom: '5px' }}>
              <HourglassEmpty style={{ fontSize: '17px' }} />
              <Typography className={classes.menuItemTextInstructions}>
                <strong style={{ paddingRight: '5px' }}>To Waitlist</strong>
                Users will be assigned to the activity waitlist when their application is approved
              </Typography>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', paddingBottom: '5px' }}>
              <Close style={{ fontSize: '17px' }} />
              <Typography className={classes.menuItemTextInstructions}>
                <strong style={{ paddingRight: '5px' }}>Reject</strong>
                Users will be rejected from this activity, but able to apply again in future
              </Typography>
            </Box>
          </Typography>
        </ListItemText>
      </ListItem>
    </List>
  );
};

const formatTimeShort = (time: string | null): string => {
  if (!time) return '';
  try {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'pm' : 'am';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  } catch {
    return time || '';
  }
};

const EventSelectionsEditor = ({
  appliedActivity,
  reload,
  isEditing = false,
  onRegisterSave,
}: {
  appliedActivity: ApplicationActivities;
  reload?: () => void;
  isEditing?: boolean;
  onRegisterSave?: (handler: () => void) => void;
}) => {
  const { theme } = useCampfireTheme();
  const apiUrl = useApiUrl();
  const orgInfo = useOrgInfo();
  const campfireMutation = useCampfireFetch({ defer: true });
  const { setSnackbar } = useSnackbar();

  const rawSelections = (appliedActivity as any)?.eventSelections;
  const parsed: Array<{ date: string; sessionIds: string[]; backupSessionIds?: string[] }> = useMemo(() => {
    if (!rawSelections) return [];
    try {
      return typeof rawSelections === 'string' ? JSON.parse(rawSelections) : rawSelections;
    } catch {
      return [];
    }
  }, [rawSelections]);

  const backupByDate = useMemo(() => {
    const map: Record<string, string[] | undefined> = {};
    parsed.forEach((sel) => {
      map[sel.date.split('T')[0]] = sel.backupSessionIds;
    });
    return map;
  }, [parsed]);

  const [selections, setSelections] = useState<Record<string, string[]>>(() =>
    parsed.reduce<Record<string, string[]>>((acc, sel) => {
      acc[sel.date.split('T')[0]] = sel.sessionIds;
      return acc;
    }, {})
  );

  React.useEffect(() => {
    if (onRegisterSave) {
      onRegisterSave(() => handleSave());
    }
  }, [selections]);

  const templates = appliedActivity.activity.rosterTemplates ?? [];
  const sessions = appliedActivity.activity.sessions.filter((s) => !s.dateRemoved);
  const dates =
    templates.length > 0
      ? templates.map((t) => String(t.rosterDate).split('T')[0]).sort()
      : appliedActivity.activity.startDate
      ? [String(appliedActivity.activity.startDate).split('T')[0]]
      : [];

  const maxSessions = orgInfo?.maxSessionsPerVolunteer ?? null;
  const sessionLimit = appliedActivity.activity.hasOpenRoster && maxSessions != null ? maxSessions : null;
  const showSelectionOrder = sessionLimit !== null;

  const rankMap = useMemo(() => {
    const map = new Map<string, number>();
    let rank = 0;
    Object.keys(selections)
      .sort()
      .forEach((date) => {
        (selections[date] ?? []).forEach((sessionId) => {
          rank += 1;
          map.set(`${date}|${sessionId}`, rank);
        });
      });
    return map;
  }, [selections]);

  const toggleSession = (date: string, sessionId: string) => {
    if (!isEditing) return;
    setSelections((prev) => {
      const current = prev[date] ?? [];
      const updated = current.includes(sessionId) ? current.filter((id) => id !== sessionId) : [...current, sessionId];
      return { ...prev, [date]: updated };
    });
  };

  const handleSave = () => {
    const payload = Object.entries(selections)
      .filter(([, sIds]) => sIds.length > 0)
      .map(([date, sessionIds]) => ({ date, sessionIds }));

    campfireMutation
      .run({
        url: `${apiUrl}/vm/activity/event/attendance/save`,
        method: 'post',
        data: {
          activityId: appliedActivity.activity.activityId,
          selections: payload,
        },
      })
      .then((response) => {
        if (response.ok) {
          setSnackbar({ variant: 'success', open: true, message: 'Event selections updated' });
          if (reload) reload();
        } else {
          setSnackbar({ variant: 'error', open: true, message: 'Error updating selections' });
        }
      });
  };

  return (
    <Box paddingTop='10px'>
      <Typography
        style={{
          fontWeight: 600,
          fontSize: '14px',
          color: '#444',
          paddingBottom: '8px',
        }}
      >
        Event Sessions
      </Typography>

      <Box
        display='flex'
        style={{ gap: 12, overflowX: 'auto', overflowY: 'hidden', width: 0, minWidth: '100%' }}
        paddingBottom={1}
      >
        {dates.map((date) => {
          const dt = DateTime.fromISO(date);
          const selectedForDate = selections[date] ?? [];

          return (
            <Box
              key={date}
              style={{
                minWidth: 180,
                borderRadius: 12,
                border: `1px solid ${
                  selectedForDate.length > 0 ? theme.palette.primary.main : theme.color.grey.border
                }`,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <Box
                style={{
                  padding: '10px 14px',
                  background:
                    selectedForDate.length > 0 ? theme.palette.primary.main : theme.color.grey.neutralBrand100,
                  cursor: 'default',
                }}
              >
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Box>
                    <Typography
                      style={{
                        fontWeight: 800,
                        fontSize: 20,
                        color: selectedForDate.length > 0 ? '#fff' : theme.color.grey.neutral400,
                        lineHeight: 1.1,
                      }}
                    >
                      {dt.toFormat('d')}
                    </Typography>
                    <Typography
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: selectedForDate.length > 0 ? 'rgba(255,255,255,0.8)' : theme.color.grey.neutral200,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {dt.toFormat('MMM yyyy')}
                    </Typography>
                  </Box>
                  <Typography
                    style={{
                      fontSize: 10,
                      color: selectedForDate.length > 0 ? 'rgba(255,255,255,0.7)' : theme.color.grey.neutral200,
                    }}
                  >
                    {dt.toFormat('ccc')}
                  </Typography>
                </Box>
              </Box>

              <Box padding='6px'>
                {sessions.map((session) => {
                  const isSelected = selectedForDate.includes(session.sessionId);
                  const rank = rankMap.get(`${date}|${session.sessionId}`);
                  const backupIds = backupByDate[date];
                  const isBackup =
                    isSelected &&
                    (backupIds !== undefined
                      ? backupIds.includes(session.sessionId)
                      : sessionLimit !== null && rank !== undefined && rank > sessionLimit);
                  const timeLabel =
                    session.startTime && session.endTime
                      ? `${formatTimeShort(session.startTime)} - ${formatTimeShort(session.endTime)}`
                      : null;

                  return (
                    <Box
                      key={session.sessionId}
                      display='flex'
                      alignItems='center'
                      style={{
                        padding: '6px',
                        borderRadius: 6,
                        cursor: isEditing ? 'pointer' : 'default',
                        background: isSelected && !isBackup ? 'rgba(121, 111, 255, 0.06)' : 'transparent',
                      }}
                      onClick={() => toggleSession(date, session.sessionId)}
                    >
                      {showSelectionOrder ? (
                        <Box
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                          flexShrink={0}
                          style={{
                            width: 18,
                            height: 18,
                            margin: 6,
                            borderRadius: 3,
                            border: `2px solid ${
                              isSelected && !isBackup ? theme.palette.primary.main : theme.color.grey.border
                            }`,
                            backgroundColor: isSelected && !isBackup ? theme.palette.primary.main : 'transparent',
                          }}
                        >
                          <Typography
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              lineHeight: 1,
                              color: isSelected && !isBackup ? '#fff' : theme.color.grey.neutral300,
                            }}
                          >
                            {isSelected ? rank : ''}
                          </Typography>
                        </Box>
                      ) : (
                        <Checkbox
                          checked={isSelected}
                          color='primary'
                          size='small'
                          disabled={!isEditing}
                          style={{ padding: 4 }}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSession(date, session.sessionId)}
                        />
                      )}
                      <Box marginLeft='4px'>
                        <Box display='flex' alignItems='center' style={{ gap: 6 }}>
                          <Typography style={{ fontSize: 12, fontWeight: 600, color: '#333', lineHeight: 1.3 }}>
                            {session.name}
                          </Typography>
                          {isBackup && (
                            <Typography
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                letterSpacing: 0.3,
                                textTransform: 'uppercase',
                                color: theme.color.grey.neutral300,
                                border: `1px solid ${theme.color.grey.border}`,
                                borderRadius: 3,
                                padding: '0 4px',
                              }}
                            >
                              Backup
                            </Typography>
                          )}
                        </Box>
                        {timeLabel && (
                          <Typography style={{ fontSize: 10, color: theme.color.grey.neutral200 }}>
                            {timeLabel}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
